// Application Status/Monitoring API
// Extracted from applicationApi.ts — status fetching and WebSocket status streaming.

import {
  ApplicationEvents,
  ApplicationEndpoints,
} from "@generated/application";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { EventBus } from "src/platform/events/EventBus";
import API from "src/platform/http/shared-http";
import debugLogger from "src/platform/utils/debugLogger";

import {
  ApplicationStatusData,
  ApplicationEventPayload,
} from "../types/application.types";

export async function getApplicationStatus(
  eventBus: EventBus,
  alias: string,
): Promise<ApplicationStatusData> {
  try {
    debugLogger.info(
      "application-api",
      `Fetching application status: ${alias}`,
    );

    const api = new API(
      apiBaseUrls.BACKEND,
      ApplicationEndpoints.GET_APPLICATION_STATUS.replace(
        ":id",
        encodeURIComponent(alias),
      ),
    );
    const response = await api.get<ApplicationStatusData>();

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_STATUS_UPDATED, {
      applicationAlias: alias,
      status: response,
    } as ApplicationEventPayload);

    debugLogger.info("application-api", `Application status loaded: ${alias}`);
    return response;
  } catch (error) {
    const errorMessage = `Failed to fetch application status: ${alias}`;
    debugLogger.error("application-api", errorMessage, error);

    eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
      error: errorMessage,
    } as ApplicationEventPayload);

    throw error;
  }
}

export function connectToStatusWebSocket(
  eventBus: EventBus,
  alias: string,
): WebSocket {
  const token = orgSessionManager.getToken();
  const wsUrl = apiBaseUrls.WS || "ws://127.0.0.1:8000";
  const ws = new WebSocket(`${wsUrl}/status/${alias}`);

  ws.onopen = () => {
    ws.send(JSON.stringify({ alias, token }));
  };

  ws.onmessage = (event) => {
    try {
      const statusData: ApplicationStatusData = JSON.parse(
        event.data as string,
      );
      eventBus.emitGlobal(ApplicationEvents.APPLICATION_STATUS_UPDATED, {
        applicationAlias: alias,
        status: statusData,
      } as ApplicationEventPayload);
    } catch (error) {
      debugLogger.error(
        "application-api",
        "Failed to parse WebSocket message",
        error,
      );
    }
  };

  return ws;
}
